import * as Vue from 'vue';
import * as VueRouter from 'vue-router';
// @ts-ignore
import { NioApmPlus } from '@nio-fe/nio-apm-plus';
import LegoInstall, * as Lego from '@nio-fe/lego';
import Axios from 'axios';
import Dayjs from 'dayjs';
import { Component, createApp } from 'vue';
import { setupStore } from '@/store';
import { setupRouter } from '@/router';
import { setupDirectives } from '@/directives';
import { setupI18n } from '@/locales/helper/setupI18n';

import { NIconSVG } from '@nio-fe/icon';

import useGlobalStore from '@/store/globalStore';
import useMatestoreStore from '@/store/matestoreStore';
import { setupMicroAppGlobal } from '@/utils/micro';
import SensorsVue from '@/directives/sensors';
import MicroDialog from '@/components/micro-dialog';
import MessageHandler from '@/utils/messageHandler';
import { getEnvironmentFromHostname } from '@/utils';
// import sensors from 'sa-sdk-javascript';
import { initWaterMarker } from '@/utils/watermark';
import NProgress from 'nprogress';
import { useGlobalRouteStore } from '@/store/globalRouteStore';
import { initStats } from '@/utils/stats';

type AppInitializeOptions = {
  base: string;
  disablePrefetch?: boolean;
  iframeMode?: boolean;
};

const release = window?.process?.env?.PUBLIC_LUBAN_CI_VERSION || 'local.1.1';
console.log('current release version', release);

export const includeThirdPartyLibs = () => {
  const win: any = window;
  win.Vue3 = Vue;
  win.Axios = Axios;
  win.Dayjs = Dayjs;
  win.VueRouter = VueRouter;
  Object.keys(Lego).forEach((key) => {
    LegoInstall[key] = Lego[key];
  });
  win.Lego = LegoInstall;
};

export const initAMapContext = () => {
  const win: any = window;
  win.__MATE_AMAP_CONTEXT = {
    secureKey: '40558953715bf09d49dc65769c4b6ce4',
    key: '55f594f57263aa0d1614bd22160b2afb',
    version: '2.0',
  };
};

export const initAPMPlus = (appInstance: any, user: any, env) => {
  const PID_MAPPING = [
    {
      pattern: /\/repair-outbound-inout\/(.+?)/,
      pid: '/repair-outbound-inout/:id',
    },
  ];
  NioApmPlus.createDefaultInstanceSync({
    aid: 1423726522,
    token: '315b3d226f584a5bb285e5f4c5258b3e',
    release,
    env,
    userId: user?.domainAccount,
    isDebug: false,
    plugins: {
      // pageview 插件具体配置可根据不同场景参考
      // https://nio.feishu.cn/docx/Ua72df3ElobAvpxSTU0cZ1hpnTd#doxcnXHuwWY3BVX6vdUBmIhpRmg
      pageview: {
        routeMode: 'history',
        extractPid: (url) => {
          const matched = PID_MAPPING.find((i) => i.pattern.test(url));
          if (matched) {
            return matched.pid;
          }
          return new URL(url).pathname;
        },
      },
      vueErrorHandler: {
        app: appInstance, // const app = createApp(); vue 3 应用传这个
      },
      blankScreen: {
        rootSelector: '.mate-micro-container',
        threshold: 1.5,
        screenshot: true,
      },
    },
  });
};
export const appInitialize = async (
  AppContainer: Component,
  options: AppInitializeOptions = { base: '/', iframeMode: false }
) => {
  const isExternal = (import.meta.env as any)?.PUBLIC_MATE_EXTERNAL;
  if (isExternal) {
    window.__MATE_EXTERNAL = isExternal;
  }

  window.__MATE_APP_OPTIONS = options;
  includeThirdPartyLibs();
  initAMapContext();
  localStorage.setItem('lego-sensor-disabled', '1');
  NProgress.configure({ showSpinner: false });
  // sensors.init(
  //   Object.assign(
  //     {},
  //     {
  //       server_url: `https://sensors.nio.com/sa?project=${
  //         getEnvironmentFromHostname() === 'prod'
  //           ? 'nio_business_prod'
  //           : 'nio_business_test'
  //       }`,
  //     }
  //   )
  // );

  const app = createApp(AppContainer);

  // 配置store
  setupStore(app);

  // 配置指令
  setupDirectives(app);

  // 配置国际化
  setupI18n(app);

  // 配置Logo组件
  app.component('NIconSVG', NIconSVG);
  app.use(MicroDialog);

  // 主数据
  await useGlobalStore().appInit();

  // 初始化router data
  const user = useGlobalStore().userInfo;
  const administrator = useGlobalStore().getBrandAdministrator();
  const brands = useGlobalStore().getAvailableBrands();
  app.use(LegoInstall);

  await useMatestoreStore().initMicro(options);
  // 微应用
  setupMicroAppGlobal();
  const waterMarkerText = [
    (user?.displayName || '').replace(/[（）]/g, ''),
    administrator?.domainAccount || '',
  ];
  if (getEnvironmentFromHostname() !== 'prod') {
    const text = window.location.host.split('.')[0].replace('mate-', '');
    if (text) {
      waterMarkerText.push(text);
    }
  }
  console.log('waterMarkerText', waterMarkerText);
  initWaterMarker(waterMarkerText);
  // 配置路由
  const router = setupRouter(app, options);
  try {
    app.use(SensorsVue(user, administrator?.organizations, brands));
  } catch (e: any) {
    console.log('sensors init error', e.message);
  }

  // sdk
  const messageHandler = new MessageHandler(router);

  app.provide('messageHandler', messageHandler);
  window.__MESSAGE_HANDLER = messageHandler;

  useGlobalRouteStore().initialize(options);

  initAPMPlus(app, user, getEnvironmentFromHostname());
  initStats(document.body);
  // initSentry(app, user, getEnvironmentFromHostname(), router);
  app.mount('#titan-main-app');

  return app;
};
